import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Target, Users, TrendingUp, CheckCircle, Zap, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HomePage = () => {
  const features = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Rastreamento de Metas',
      description: 'Monitore o progresso em direção aos seus objetivos de negócios com barras de progresso visuais e atualizações em tempo real.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Gestão de Clientes',
      description: 'Acompanhe serviços, prazos e relacionamentos com clientes em um local centralizado.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Análise de Desempenho',
      description: 'Obtenha insights com ferramentas abrangentes de análise e relatórios para tomar decisões melhores.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Gestão de Tarefas',
      description: 'Organize tickets e tarefas com rastreamento de status, prioridades e capacidades de atribuição.'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Rastreamento de Implementações',
      description: 'Monitore implementações de sistema com checklists detalhados e rastreamento de progresso.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Seguro e Confiável',
      description: 'Segurança de nível empresarial com controle de acesso baseado em funções para proteger seus dados.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard Center - Gestão Empresarial Completa</title>
        <meta name="description" content="Transforme suas operações de negócios com o Dashboard Center - a solução tudo-em-um para rastreamento de metas, gestão de clientes e colaboração em equipe." />
      </Helmet>

      <div className="min-h-screen bg-black">
        {/* Navigation */}
        <nav className="bg-white/5 backdrop-blur-lg border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-8 h-8 text-orange-500" />
                <span className="text-xl font-bold text-white">Dashboard Center</span>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/pricing">
                  <Button variant="ghost" className="text-white hover:text-orange-300">
                    Preços
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-orange-300">
                    Entrar
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                    Começar
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-20 pb-32 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
                Gerencie Seu Negócio
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
                  Em Um Só Lugar
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
                O Dashboard Center fornece tudo o que você precisa para rastrear metas, gerenciar clientes, monitorar implementações e impulsionar seu negócio com insights poderosos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                    Teste Grátis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg">
                    Ver Preços
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Recursos Poderosos
              </h2>
              <p className="text-xl text-gray-300">
                Tudo o que você precisa para otimizar suas operações
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 border border-gray-200 hover:border-orange-500 transition-all hover:transform hover:scale-105"
                >
                  <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center text-white mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl p-12 text-center"
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                Pronto para Transformar Seu Negócio?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Junte-se a milhares de equipes que já usam o Dashboard Center
              </p>
              <Link to="/register">
                <Button className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg">
                  Começar Agora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;